import React, { useEffect, useState } from "react";
import { Loader2, BookOpen, Utensils, Eye, Heart, Sparkles, Euro } from "lucide-react";
import { fetchAdminStats } from "../../lib/api";
import { toast } from "../../hooks/use-toast";

// ---------------- Stats editor ----------------
const LANG_LABELS = { it: "Italiano", en: "English", es: "Español", fr: "Français", de: "Deutsch" };
const RANGE_OPTIONS = [
  { days: 7,  label: "7 giorni" },
  { days: 30, label: "30 giorni" },
  { days: 90, label: "90 giorni" },
  { days: 365, label: "1 anno" },
];

export default function StatsEditor() {
  const [range, setRange] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await fetchAdminStats(range);
        if (alive) setData(r);
      } catch (e) {
        toast({ title: "Errore statistiche", description: e?.message });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [range]);

  if (loading || !data) {
    return <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" /></div>;
  }

  const t = data.totals;
  const spa = data.spa;

  return (
    <div data-testid="stats-editor">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h2 className="font-serif text-2xl font-bold text-[#2b1410]">Statistiche</h2>
        <div className="ml-auto inline-flex rounded-full bg-white border border-[#e8dcc8] p-0.5">
          {RANGE_OPTIONS.map((o) => (
            <button
              key={o.days}
              onClick={() => setRange(o.days)}
              className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-full transition-colors ${
                range === o.days ? "bg-[#4a1c17] text-[#f7ebdf]" : "text-[#4a1c17] hover:bg-[#faf3ee]"
              }`}
              data-testid={`stats-range-${o.days}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <KpiCard icon={<Eye className="h-4 w-4" />} label="Home viste" value={t.home} tone="burgundy" />
        <KpiCard icon={<BookOpen className="h-4 w-4" />} label="Menu aperti" value={t.menu} tone="burgundy" />
        <KpiCard icon={<Utensils className="h-4 w-4" />} label="Piatti visti" value={t.dish} tone="burgundy" />
        <KpiCard icon={<Heart className="h-4 w-4" />} label="Aggiunti selez." value={t.add} tone="amber" />
        <KpiCard icon={<Sparkles className="h-4 w-4" />} label="Spa aperture" value={t.spa} tone="emerald" />
        <KpiCard icon={<Euro className="h-4 w-4" />} label="Ricavi Spa" value={`€ ${spa.revenue_eur}`} sub={`${spa.total} prenotazioni · ${spa.upcoming} future`} tone="emerald" />
      </div>

      {/* Daily trend */}
      <StatsCard title="Andamento giornaliero" subtitle="Visite home + menu + piatti">
        <DailyBarChart daily={data.daily} />
      </StatsCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {/* Top viewed dishes */}
        <StatsCard title="Piatti più visti" subtitle="Numero di visualizzazioni">
          <TopList items={data.top_viewed} emptyLabel="Nessuna visualizzazione registrata nel periodo" tone="burgundy" />
        </StatsCard>
        {/* Top added dishes */}
        <StatsCard title='Più aggiunti a "La mia selezione"' subtitle="I preferiti dei clienti">
          <TopList items={data.top_added} emptyLabel='Nessun cliente ha ancora aggiunto piatti alla selezione' tone="amber" />
        </StatsCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {/* Top menus */}
        <StatsCard title="Menu più aperti" subtitle="Top-level menu">
          <MenuBarList items={data.top_menus} />
        </StatsCard>
        {/* Languages */}
        <StatsCard title="Lingue usate dai clienti">
          <LangBarList items={data.langs} />
        </StatsCard>
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, sub, tone = "burgundy" }) {
  const toneMap = {
    burgundy: "bg-[#4a1c17] text-[#f7ebdf]",
    amber: "bg-amber-500 text-white",
    emerald: "bg-emerald-600 text-white",
  };
  return (
    <div className="rounded-2xl bg-white border border-[#e8dcc8] p-3 shadow-sm" data-testid="stats-kpi">
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${toneMap[tone]}`}>{icon}</span>
        <span className="text-[11px] uppercase tracking-wider text-[#7c5a4e] font-semibold">{label}</span>
      </div>
      <div className="font-serif text-2xl font-bold text-[#2b1410]">{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-[#7c5a4e]">{sub}</div>}
    </div>
  );
}

function StatsCard({ title, subtitle, children }) {
  return (
    <div className="rounded-2xl bg-white border border-[#e8dcc8] p-4 shadow-sm">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h3 className="font-serif text-lg font-bold text-[#2b1410]">{title}</h3>
          {subtitle && <p className="text-xs text-[#7c5a4e]">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function DailyBarChart({ daily }) {
  const max = Math.max(1, ...daily.map((d) => d.home + d.menu + d.dish));
  return (
    <div className="flex items-end gap-0.5 h-32 overflow-x-auto" data-testid="stats-daily-chart">
      {daily.map((d) => {
        const total = d.home + d.menu + d.dish;
        const h = Math.round((total / max) * 100);
        return (
          <div key={d.day} className="flex-1 min-w-[6px] flex flex-col items-center gap-1 group relative">
            <div className="w-full flex items-end" style={{ height: "100%" }}>
              <div
                className="w-full rounded-t bg-[#4a1c17] transition-all hover:bg-[#2b1410]"
                style={{ height: `${h}%` }}
                title={`${d.day}: ${total} eventi (home ${d.home} · menu ${d.menu} · piatti ${d.dish})`}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TopList({ items, emptyLabel, tone = "burgundy" }) {
  if (!items || items.length === 0) {
    return <p className="text-sm text-[#7c5a4e] py-6 text-center">{emptyLabel}</p>;
  }
  const max = Math.max(1, ...items.map((it) => it.count));
  const barTone = tone === "amber" ? "bg-amber-500" : "bg-[#4a1c17]";
  return (
    <ol className="space-y-2" data-testid="stats-top-list">
      {items.map((it, i) => (
        <li key={it.id} className="flex items-center gap-2 text-sm">
          <span className="w-5 text-xs font-semibold text-[#7c5a4e] tabular-nums">{i + 1}.</span>
          {it.image ? (
            <img src={it.image} alt="" className="h-7 w-7 rounded object-cover shrink-0" />
          ) : (
            <div className="h-7 w-7 rounded bg-[#faf3ee] shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[#2b1410] truncate">{it.name}</div>
            <div className="mt-0.5 h-1.5 rounded-full bg-[#faf3ee]">
              <div className={`h-full rounded-full ${barTone}`} style={{ width: `${(it.count / max) * 100}%` }} />
            </div>
          </div>
          <span className="text-sm font-semibold text-[#4a1c17] tabular-nums w-10 text-right">{it.count}</span>
        </li>
      ))}
    </ol>
  );
}

function MenuBarList({ items }) {
  if (!items || items.length === 0) {
    return <p className="text-sm text-[#7c5a4e] py-6 text-center">Nessuna apertura menu registrata</p>;
  }
  const max = Math.max(1, ...items.map((it) => it.count));
  return (
    <ol className="space-y-2" data-testid="stats-menu-list">
      {items.map((it, i) => (
        <li key={it.id} className="flex items-center gap-2 text-sm">
          <span className="w-5 text-xs font-semibold text-[#7c5a4e] tabular-nums">{i + 1}.</span>
          <div className="flex-1 min-w-0">
            <div className="text-[#2b1410] truncate">{it.name}</div>
            <div className="mt-0.5 h-1.5 rounded-full bg-[#faf3ee]">
              <div className="h-full rounded-full bg-[#4a1c17]" style={{ width: `${(it.count / max) * 100}%` }} />
            </div>
          </div>
          <span className="text-sm font-semibold text-[#4a1c17] tabular-nums w-10 text-right">{it.count}</span>
        </li>
      ))}
    </ol>
  );
}

function LangBarList({ items }) {
  if (!items || items.length === 0) {
    return <p className="text-sm text-[#7c5a4e] py-6 text-center">Nessun cambio lingua registrato</p>;
  }
  const total = items.reduce((s, x) => s + x.count, 0) || 1;
  const palette = ["#4a1c17", "#a86a4f", "#8a6d3b", "#5b4636", "#3f2b23"];
  return (
    <div data-testid="stats-lang-list">
      <div className="flex h-2 rounded-full overflow-hidden bg-[#faf3ee] mb-3">
        {items.map((it, i) => (
          <div key={it.lang} style={{ width: `${(it.count / total) * 100}%`, backgroundColor: palette[i % palette.length] }} />
        ))}
      </div>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={it.lang} className="flex items-center gap-2 text-sm">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: palette[i % palette.length] }} />
            <span className="flex-1 text-[#2b1410]">{LANG_LABELS[it.lang] || it.lang}</span>
            <span className="text-[#7c5a4e] tabular-nums">{it.count}</span>
            <span className="w-12 text-right text-xs text-[#7c5a4e]">{Math.round((it.count / total) * 100)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
